

































































































































































































































































import { Component, Vue, PropSync, Ref, Prop, Watch } from 'vue-property-decorator';
import { IRules } from '@/components/UI/Form/ValidationModule';
import SForm from '@/components/UI/Form/SForm.vue';
import { validateMax, validateRequired } from '@/components/UI/Form/Validations';
import { ALL_CURRENCIES } from '@/queries/Currency';
import { SmartQuery } from 'vue-apollo-decorator';
import { GQL_allCurrencies_allCurrencies } from '@/queries/__generated__/allCurrencies';
import { GQL_orderInvoiceStartNumberSet_orderInvoiceStartNumberSet } from '@/queries/__generated__/orderInvoiceStartNumberSet';
import formatBBAN from '@/filters/formatBBAN';
import Locale from '@/utils/locale';
import { GQL_SupplierPartyReferenceSettingEnum, GQL_UnitSettingBase, GQL_RoundingTypeEnum } from '@/queries/global';
import { AVAILABLE_LEGAL_NOTES, EFAKTURA_KID_LENGTH, OVERDUE_NOTICE_MAX_CHARGE_AMOUNT } from '@/app/Invoice';
import { ALL_COUNTRIES } from '@/queries/Country';
import { GQL_allCountries_allCountries } from '@/queries/__generated__/allCountries';
import gql from 'graphql-tag';
import { UNITS_OF_MEASURE } from '@/queries/UnitOfMeasure';
import { GQL_unitsOfMeasure_unitsOfMeasure } from '@/queries/__generated__/unitsOfMeasure';
import { EFAKTURA_SETUP } from '@/queries/Efaktura';

const ORDER_INVOICE_START_NUMBER_SET = gql`
  query orderInvoiceStartNumberSet {
    orderInvoiceStartNumberSet {
      orderNumberSet
      invoiceNumberSet
    }
  }
`;

@Component({})
export default class CompanyInvoiceSettingsForm extends Vue {
  @SmartQuery(ALL_COUNTRIES) private allCountries!: GQL_allCountries_allCountries[];
  @SmartQuery(ALL_CURRENCIES) private allCurrencies!: GQL_allCurrencies_allCurrencies[];
  @SmartQuery(UNITS_OF_MEASURE) private unitsOfMeasure!: GQL_unitsOfMeasure_unitsOfMeasure[];
  @SmartQuery(EFAKTURA_SETUP) private efakturaSetup!: boolean;
  @SmartQuery({
    query: ORDER_INVOICE_START_NUMBER_SET,
    fetchPolicy: 'no-cache',
  }) private orderInvoiceStartNumberSet!: GQL_orderInvoiceStartNumberSet_orderInvoiceStartNumberSet;

  @Prop({ type: Object, required: true }) private formData!: {
    unitSetting: GQL_UnitSettingBase;
  };
  @PropSync('valid', { type: Boolean, default: true }) private validForm!: boolean;

  @Ref() private readonly formRef?: SForm;

  private formatBBAN = formatBBAN;
  private taxInclusiveAmountRoundings = Object.keys(GQL_RoundingTypeEnum);

  private getCountryCodeFromSwift(swift?: string): string | null {
    if (!swift) return null;
    const countryCode = swift.slice(4,6);
    return countryCode;
  }

  // Enabling eFaktura forces a compatible KID scheme: a 16-character KID with a mod11
  // check digit, whose first six digits are the customer number. The backend enforces
  // the same rule; here we set the values and lock the KID controls in the template.
  private onEfakturaToggle() {
    const setting = this.formData.unitSetting.unitInvoiceSetting!;
    if (setting.efakturaEnabled) {
      setting.usesKid = true;
      setting.kidModDigit = 11;
      setting.kidLength = EFAKTURA_KID_LENGTH;
    }
  }

  private toggleLegalNote(noteKey: string, value: boolean) {
    const legalNotes = this.formData.unitSetting.unitInvoiceSetting!.legalNotes!;
    if (value) {
      legalNotes.push(noteKey);
    } else {
      if(legalNotes.includes(noteKey)) {
        legalNotes.splice(legalNotes.indexOf(noteKey), 1);
      }
    }
  }

  private availableLegalNotes = AVAILABLE_LEGAL_NOTES;

  private supplierPartyReferenceSettingTypes = Object.keys(GQL_SupplierPartyReferenceSettingEnum);

  private rules: IRules = {
    usesKid: [], // Used for triggerBy for kidLength and kidModDigit
    kidLength: [ {
      validation: values => {
        const { formElements } = values;
        if (!formElements?.usesKid?.dataValue) return true;
        return validateRequired()(values);
      },
      message: i18n => i18n.t('Error.required', { field: this.t_attr('UnitInvoiceSetting', 'kidLength') }),
      triggerBy: [ 'usesKid' ],
    } ],
    kidModDigit: [ {
      validation: values => {
        const { formElements } = values;
        if (!formElements?.usesKid?.dataValue) return true;
        return validateRequired()(values);
      },
      message: i18n => i18n.t('Error.required', { field: this.t_attr('UnitInvoiceSetting', 'kidModDigit') }),
      triggerBy: [ 'usesKid' ],
    } ],
    customerCountry: {
      code: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('UnitInvoiceSetting', 'customerCountry') }) } ],
    },
    currency: { id: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('UnitInvoiceSetting', 'currency') }) } ] },
    customerCreditDays: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('UnitInvoiceSetting', 'customerCreditDays') }) } ],
    startingOrderNumber: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('UnitInvoiceSetting', 'orderStartNo') }) } ],
    startingInvoiceNumber: [ { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('UnitInvoiceSetting', 'invoiceStartNo') }) } ],
    usesOverdueNotices: [],
    overdueNoticeCharge: [ {
      validation: values => {
        const { formElements } = values;
        if (!formElements?.usesOverdueNotices?.dataValue) return true;
        return validateMax(OVERDUE_NOTICE_MAX_CHARGE_AMOUNT)(values);
      },
      message: i18n => i18n.t('Error.maxAmount', { amount: OVERDUE_NOTICE_MAX_CHARGE_AMOUNT }),
      triggerBy: [ 'usesOverdueNotices' ],
    } ],
    usesInterestInvoices: [],
    orderSaveLockConfirmation: [],
  };

  private get locale() {
    return Locale.get();
  }

  @Watch('formData.unitSetting.unitInvoiceSetting.usesOverdueNotices')
  private setCorrectOverdueNoticeCharge(): void {
    if (this.formData.unitSetting.unitInvoiceSetting) {
      this.formData.unitSetting.unitInvoiceSetting.overdueNoticeCharge = this.formData.unitSetting.unitInvoiceSetting.usesOverdueNotices ? OVERDUE_NOTICE_MAX_CHARGE_AMOUNT : null;
    }
  }
}
